/**
 * useResizable — edge/corner resize + body drag for floating windows
 *
 * Renders 8 invisible hit zones around the container perimeter for resize,
 * and provides drag handle props for repositioning the body.
 * All pointer tracking uses document-level listeners for smooth tracking
 * even when the cursor moves outside the element.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

type Edge = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

interface Rect {
  width: number;
  height: number;
  x: number;
  y: number;
}

interface UseResizableOptions {
  initialWidth: number;
  initialHeight: number;
  initialX: number;
  initialY: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  onResizeEnd: (rect: Rect) => void;
  onMoveEnd: (rect: Rect) => void;
}

interface UseResizableReturn {
  width: number;
  height: number;
  x: number;
  y: number;
  isResizing: boolean;
  isDragging: boolean;
  resizeHandles: ReactNode;
  dragHandleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
    style: { cursor: string };
  };
}

// ── Utilities ──────────────────────────────────────────────────────────────────

const EDGE_SIZE = 8;
const CORNER_SIZE = 12;
const MIN_VISIBLE = 100;

const EDGES: Edge[] = ['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se'];

const CURSOR_MAP: Record<Edge, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  nw: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  se: 'nwse-resize',
};

/** Clamp a rect so at least MIN_VISIBLE px remain visible in the viewport. */
export function clampToViewport(rect: Rect, vw: number, vh: number): Rect {
  const x = Math.max(-rect.width + MIN_VISIBLE, Math.min(rect.x, vw - MIN_VISIBLE));
  const y = Math.max(-rect.height + MIN_VISIBLE, Math.min(rect.y, vh - MIN_VISIBLE));
  return { ...rect, x, y };
}

function getPointerPosition(e: MouseEvent | TouchEvent): { clientX: number; clientY: number } {
  if ('touches' in e) {
    const touch = e.touches[0] ?? e.changedTouches[0];
    return { clientX: touch.clientX, clientY: touch.clientY };
  }
  return { clientX: e.clientX, clientY: e.clientY };
}

// ── Handle styles ──────────────────────────────────────────────────────────────

function edgeStyle(edge: Edge): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    zIndex: 2,
    cursor: CURSOR_MAP[edge],
  };

  switch (edge) {
    case 'n':
      return { ...base, top: -EDGE_SIZE / 2, left: CORNER_SIZE, right: CORNER_SIZE, height: EDGE_SIZE };
    case 's':
      return { ...base, bottom: -EDGE_SIZE / 2, left: CORNER_SIZE, right: CORNER_SIZE, height: EDGE_SIZE };
    case 'w':
      return { ...base, left: -EDGE_SIZE / 2, top: CORNER_SIZE, bottom: CORNER_SIZE, width: EDGE_SIZE };
    case 'e':
      return { ...base, right: -EDGE_SIZE / 2, top: CORNER_SIZE, bottom: CORNER_SIZE, width: EDGE_SIZE };
    case 'nw':
      return { ...base, top: -CORNER_SIZE / 2, left: -CORNER_SIZE / 2, width: CORNER_SIZE, height: CORNER_SIZE };
    case 'ne':
      return { ...base, top: -CORNER_SIZE / 2, right: -CORNER_SIZE / 2, width: CORNER_SIZE, height: CORNER_SIZE };
    case 'sw':
      return { ...base, bottom: -CORNER_SIZE / 2, left: -CORNER_SIZE / 2, width: CORNER_SIZE, height: CORNER_SIZE };
    case 'se':
      return { ...base, bottom: -CORNER_SIZE / 2, right: -CORNER_SIZE / 2, width: CORNER_SIZE, height: CORNER_SIZE };
  }
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useResizable(options: UseResizableOptions): UseResizableReturn {
  const {
    initialWidth,
    initialHeight,
    initialX,
    initialY,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    onResizeEnd,
    onMoveEnd,
  } = options;

  // Local rect tracks real-time position during drag/resize
  const [rect, setRect] = useState<Rect>({
    width: initialWidth,
    height: initialHeight,
    x: initialX,
    y: initialY,
  });

  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Refs for tracking drag state without triggering re-renders during move
  const dragStateRef = useRef<{
    type: 'resize' | 'move';
    edge?: Edge;
    startX: number;
    startY: number;
    startRect: Rect;
  } | null>(null);

  // Sync with external initial values when they change (e.g., Redux hydration)
  const prevInitialRef = useRef({ initialWidth, initialHeight, initialX, initialY });
  useEffect(() => {
    const prev = prevInitialRef.current;
    if (
      prev.initialWidth !== initialWidth ||
      prev.initialHeight !== initialHeight ||
      prev.initialX !== initialX ||
      prev.initialY !== initialY
    ) {
      // Only sync if not currently dragging/resizing
      if (!dragStateRef.current) {
        setRect({ width: initialWidth, height: initialHeight, x: initialX, y: initialY });
      }
      prevInitialRef.current = { initialWidth, initialHeight, initialX, initialY };
    }
  }, [initialWidth, initialHeight, initialX, initialY]);

  // ── Resize math ────────────────────────────────────────────────────────────

  const computeRect = useCallback(
    (edge: Edge, startRect: Rect, deltaX: number, deltaY: number): Rect => {
      let { x, y, width, height } = startRect;

      // North: top edge moves → y changes, height changes inversely
      if (edge === 'n' || edge === 'nw' || edge === 'ne') {
        const newHeight = Math.max(minHeight, Math.min(maxHeight, height - deltaY));
        y = y + (height - newHeight); // Anchor bottom edge
        height = newHeight;
      }

      // South: bottom edge moves → height changes
      if (edge === 's' || edge === 'sw' || edge === 'se') {
        height = Math.max(minHeight, Math.min(maxHeight, height + deltaY));
      }

      // West: left edge moves → x changes, width changes inversely
      if (edge === 'w' || edge === 'nw' || edge === 'sw') {
        const newWidth = Math.max(minWidth, Math.min(maxWidth, width - deltaX));
        x = x + (width - newWidth); // Anchor right edge
        width = newWidth;
      }

      // East: right edge moves → width changes
      if (edge === 'e' || edge === 'ne' || edge === 'se') {
        width = Math.max(minWidth, Math.min(maxWidth, width + deltaX));
      }

      return { x, y, width, height };
    },
    [minWidth, minHeight, maxWidth, maxHeight],
  );

  // ── Pointer handlers ───────────────────────────────────────────────────────

  const handlePointerMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const state = dragStateRef.current;
      if (!state) return;

      // Prevent scrolling on touch devices during drag/resize
      if ('touches' in e && e.cancelable) e.preventDefault();

      const { clientX, clientY } = getPointerPosition(e);
      const deltaX = clientX - state.startX;
      const deltaY = clientY - state.startY;

      if (state.type === 'resize' && state.edge) {
        const newRect = computeRect(state.edge, state.startRect, deltaX, deltaY);
        setRect(newRect);
      } else if (state.type === 'move') {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const newX = state.startRect.x + deltaX;
        const newY = state.startRect.y + deltaY;
        // Clamp so at least MIN_VISIBLE px remain visible
        const clampedX = Math.max(
          -state.startRect.width + MIN_VISIBLE,
          Math.min(newX, vw - MIN_VISIBLE),
        );
        const clampedY = Math.max(
          -state.startRect.height + MIN_VISIBLE,
          Math.min(newY, vh - MIN_VISIBLE),
        );
        setRect((prev) => ({ ...prev, x: clampedX, y: clampedY }));
      }
    },
    [computeRect],
  );

  const handlePointerUp = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const state = dragStateRef.current;
      if (!state) return;

      const { clientX, clientY } = getPointerPosition(e);
      const deltaX = clientX - state.startX;
      const deltaY = clientY - state.startY;

      if (state.type === 'resize' && state.edge) {
        const finalRect = computeRect(state.edge, state.startRect, deltaX, deltaY);
        setRect(finalRect);
        onResizeEnd(finalRect);
      } else if (state.type === 'move') {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const newX = state.startRect.x + deltaX;
        const newY = state.startRect.y + deltaY;
        const clampedX = Math.max(
          -state.startRect.width + MIN_VISIBLE,
          Math.min(newX, vw - MIN_VISIBLE),
        );
        const clampedY = Math.max(
          -state.startRect.height + MIN_VISIBLE,
          Math.min(newY, vh - MIN_VISIBLE),
        );
        const finalRect = {
          width: state.startRect.width,
          height: state.startRect.height,
          x: clampedX,
          y: clampedY,
        };
        setRect((prev) => ({ ...prev, x: clampedX, y: clampedY }));
        onMoveEnd(finalRect);
      }

      dragStateRef.current = null;
      setIsResizing(false);
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    },
    [computeRect, onResizeEnd, onMoveEnd],
  );

  // Attach/detach document-level listeners
  useEffect(() => {
    if (!isResizing && !isDragging) return;

    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchmove', handlePointerMove, { passive: false });
    document.addEventListener('touchend', handlePointerUp);

    return () => {
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchmove', handlePointerMove);
      document.removeEventListener('touchend', handlePointerUp);
    };
  }, [isResizing, isDragging, handlePointerMove, handlePointerUp]);

  // ── Start handlers ─────────────────────────────────────────────────────────

  const startResize = useCallback(
    (edge: Edge, e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const { clientX, clientY } = getPointerPosition(e.nativeEvent);
      dragStateRef.current = {
        type: 'resize',
        edge,
        startX: clientX,
        startY: clientY,
        startRect: { ...rect },
      };
      setIsResizing(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = CURSOR_MAP[edge];
    },
    [rect],
  );

  const startDrag = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      // Don't start drag if clicking on a button or interactive element
      const target = e.target as HTMLElement;
      if (target.closest('button, a, [role="button"], input, select, textarea')) return;

      e.preventDefault();
      const { clientX, clientY } = getPointerPosition(e.nativeEvent);
      dragStateRef.current = {
        type: 'move',
        startX: clientX,
        startY: clientY,
        startRect: { ...rect },
      };
      setIsDragging(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    },
    [rect],
  );

  // ── Resize handles (8 invisible zones) ─────────────────────────────────────

  const resizeHandles: ReactNode = useMemo(
    () => (
      <>
        {EDGES.map((edge) => (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            key={edge}
            style={edgeStyle(edge)}
            onMouseDown={(e) => startResize(edge, e)}
            onTouchStart={(e) => startResize(edge, e)}
          />
        ))}
      </>
    ),
    [startResize],
  );

  // ── Drag handle props ──────────────────────────────────────────────────────

  const dragHandleProps = useMemo(
    () => ({
      onMouseDown: startDrag,
      onTouchStart: startDrag,
      style: { cursor: isDragging ? 'grabbing' : 'grab' } as const,
    }),
    [startDrag, isDragging],
  );

  return {
    width: rect.width,
    height: rect.height,
    x: rect.x,
    y: rect.y,
    isResizing,
    isDragging,
    resizeHandles,
    dragHandleProps,
  };
}
